import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

const AUTH_ENDPOINT_RE = /(^|\/)auth\/(login|register|refresh|forgot-password|reset-password)(?:[?#/]|$)/;

// Endpoints that legitimately take longer than 15s (AI analysis, crawlers, etc.)
// Values are in milliseconds.
const SLOW_ENDPOINT_TIMEOUTS: Array<{ pattern: RegExp; ms: number }> = [
  { pattern: /\/(options-analysis|analysis)\//i,              ms: 90_000 },
  { pattern: /\/research\/(generate|analyze|report)/i,        ms: 120_000 },
  { pattern: /\/earnings\/analyze/i,                          ms: 90_000 },
  { pattern: /\/scanner\//i,                                  ms: 60_000 },
  { pattern: /\/dividend|\/sector|\/correlation/i,            ms: 60_000 },
  { pattern: /\/journal\/(generate|ai)/i,                     ms: 90_000 },
  { pattern: /\/admin\/refresh/i,                             ms: 60_000 },
];

function timeoutForUrl(url: string): number {
  for (const { pattern, ms } of SLOW_ENDPOINT_TIMEOUTS) {
    if (pattern.test(url)) return ms;
  }
  return 15_000;
}

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINT_RE.test(url);
}

function addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

export const authInterceptorFn: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip auth endpoints
  if (isAuthEndpoint(request.url)) {
    return next(request);
  }

  // Attach JWT token
  const token = authService.getAccessToken();
  if (token) {
    request = addToken(request, token);
  }

  const requestTimeout = timeoutForUrl(request.url);
  return next(request).pipe(
    timeout(requestTimeout),
    catchError((error: any) => {
      if (error instanceof TimeoutError) {
        return throwError(() => ({ status: 408, message: 'Request timed out' }));
      }
      if ((error as HttpErrorResponse).status === 401) {
        return authService.refreshToken().pipe(
          switchMap(() => {
            const newToken = authService.getAccessToken();
            if (newToken) {
              return next(addToken(request, newToken));
            }
            authService.logout();
            router.navigate(['/auth/login'], { queryParams: { reason: 'session_expired' } });
            return throwError(() => new Error('Session expired'));
          }),
          catchError(() => {
            authService.logout();
            router.navigate(['/auth/login'], { queryParams: { reason: 'session_expired' } });
            return throwError(() => new Error('Session expired. Please login again.'));
          })
        );
      }
      return throwError(() => error);
    })
  );
};
